import html2canvas from 'html2canvas';
import { formatBRL } from '@/lib/format';
import { calcParcelaFlex } from './parcela-flex';
import { jsPDF } from 'jspdf';
import { renderContainerToPdf, waitForImages } from '@/lib/htmlToPdf';

/**
 * Chave do sessionStorage onde ProposalPage espera achar o payload da proposta.
 *
 * ATENÇÃO: hoje ninguém ESCREVE nessa chave — só ProposalPage lê. A rota também
 * não está registrada no CRM.tsx, então o caminho inteiro está morto; propostas
 * de verdade passam por proposal_simulations. A constante existe porque o
 * import dela estava quebrado (o módulo não compilava). Se for reativar essa
 * tela, o lado que grava precisa ser escrito.
 */
export const PROPOSAL_STORAGE_KEY = 'north:proposal-payload';

export type MultiCotasCota = {
  numero?: number;
  mes?: number | string;
  prazo?: number | string;
  grupo?: number | string;
  categoria?: string;
  credito?: string;
  parcela?: string;
  lance_pago?: string;
  lance_embutido?: string;
  credito_liberado?: string;
  credito_liquido?: string;
  parcela_pos?: string;
  saldo_devedor?: string;
  flex?: string;
  taxa_admin?: string;
  // Agrupamento server-side: a tool projeto_estruturado colapsa cotas
  // idênticas em uma só entrada com quantidade=N. O PDF expande de
  // volta em N linhas (uma por cota) — ver expandCotasForPdf abaixo.
  quantidade?: number;
};

export type MultiCotasResumoProjeto = {
  credito_contratado_total: string;
  parcela_inicial_total: string;
  quantidade_cotas: number;
  saldo_devedor_inicial_total: string;
  credito_liberado_total: string;
  credito_liquido_total: string;
  parcela_pos_total: string;
  lance_pago_total: string;
  lance_embutido_total: string;
  aviso_vagas?: string;
};

export type MultiCotasData = {
  resumo_projeto: MultiCotasResumoProjeto;
  cotas: MultiCotasCota[];
  clientName?: string;
};

export type ProposalPayload = Record<string, unknown> & {
  groupNumber?: number | string;
  categoria?: string;
  modalidade?: string;
  prazo?: number | string;
  taxaAdmin?: number;
  valorCredito?: number;
  parcelaSemSeguro?: number;
  valorSeguro?: number;
  seguro?: string;
  juncaoCota?: string;
  lanceLivre?: number | string;
  lanceEmbutido?: number | string;
  lanceFixoPercent?: number | string;
  descontoFlex?: number | string;
  mesesContemplacao?: number;
  parcelaAPagar?: number;
  parcelasRestantes?: number;
  parcelasRestantesSorteio?: number;
  parcelasRestantesLanceFixo?: number;
  parcelasRestantesLanceLivre?: number;
  parcelaJunta?: number;
  valorLanceLivre?: number;
  valorLanceEmbutido?: number;
  valorLanceFixo?: number;
  creditoSorteio?: number;
  saldoDevedorSorteio?: number;
  parcelaAPagarSorteio?: number;
  creditoLanceFixo?: number;
  saldoDevedorLanceFixo?: number;
  parcelaAPagarLanceFixo?: number;
  creditoLanceLivre?: number;
  saldoDevedorLanceLivre?: number;
  parcelaComSeguro?: number;
  lanceFixoAtivo?: boolean;
  lanceLivreAtivo?: boolean;
  lanceFidelidadeAtivo?: boolean;
  lanceFidelidadePercent?: number | string;
  valorLanceFidelidade?: number;
  creditoLanceFidelidade?: number;
  saldoDevedorLanceFidelidade?: number;
  parcelaAPagarLanceFidelidade?: number;
  parcelasRestantesLanceFidelidade?: number;
  generatedAt?: string;
  clientName?: string;
};

export type UserInfo = {
  name?: string | null;
  email?: string | null;
  phone?: string | null;
};

const formatCurrency = (value?: number) => {
  if (typeof value !== 'number') return '-';
  return formatBRL(value);
};

const safeText = (value?: string | number | null) => {
  if (value === null || value === undefined) return '-';
  return String(value);
};

export const generateProposalPdf = async (
  payload: ProposalPayload,
  userInfo: UserInfo,
) => {
  const descontoFlexValor = Number(payload.descontoFlex ?? 0) || 0;
  const prazoNumerico = Number(payload.prazo ?? 1) || 1;
  // Parcelas restantes por cenário: o lance encurta o prazo, então cada simulação
  // tem uma contagem diferente. Records antigos só têm `parcelasRestantes` (sorteio).
  const parcelasRestantesSorteio = payload.parcelasRestantesSorteio ?? payload.parcelasRestantes ?? Math.max(0, prazoNumerico - 1);
  const parcelasRestantesLanceFixo = payload.parcelasRestantesLanceFixo ?? parcelasRestantesSorteio;
  const parcelasRestantesLanceLivre = payload.parcelasRestantesLanceLivre ?? parcelasRestantesSorteio;
  // parcelaJunta is the total parcela for all cotas (joint). Fall back to parcelaComSeguro (per-cota) for old records.
  const parcelaSelecionada = typeof payload.parcelaJunta === 'number'
    ? payload.parcelaJunta
    : typeof payload.parcelaComSeguro === 'number'
      ? payload.parcelaComSeguro
      : calcParcelaFlex({ parcelaSemSeguro: payload.parcelaSemSeguro ?? 0, valorSeguro: payload.valorSeguro ?? 0, hasInsurance: payload.seguro === 'Sim' }, descontoFlexValor);

  const origin = window.location.origin;

  const container = document.createElement('div');
  container.style.position = 'fixed';
  container.style.left = '-9999px';
  container.style.top = '-9999px';
  container.style.width = '600px';
  container.style.padding = '20px';
  container.style.background = '#ffffff';
  container.style.color = '#4a4a4a';
  container.style.fontSize = '13px';
  container.style.lineHeight = '1.4';
  container.style.pointerEvents = 'none';
  container.style.zIndex = '9999';
  container.style.fontFamily = 'Arial, sans-serif';

  // Header com logos
  const headerHtml = `
    <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px;">
      <img src="${origin}/logos/logo_full.png" alt="North" style="height: 35px; object-fit: contain;" />
      <div style="width: 2px; height: 40px; background: #354660;"></div>
      <img src="${origin}/logos/servopa_parceiro.webp" alt="Servopa" style="height: 35px; object-fit: contain;" />
    </div>
  `;

  // Slogan
  const sloganHtml = `
    <div style="margin-bottom: 10px;">
      <p style="font-size: 13px; color: #4a4a4a; margin: 0;">${payload.clientName ? `Proposta para ${safeText(payload.clientName as string)}` : 'Proposta de consórcio'}</p>
    </div>
  `;

  // Campos simples + Contato lado a lado
  const fieldsAndContactHtml = `
    <div style="display: flex; gap: 12px; margin-bottom: 10px; align-items: stretch;">
      <!-- Campos à esquerda -->
      <div style="flex: 0 0 50%;">
        <div style="display: flex; flex-direction: column; gap: 0;">
          <div style="display: flex; justify-content: space-between; align-items: center; padding: 4px 0; border-bottom: 1px solid #eee; font-size: 11px;">
            <span style="color: #6d6d6d;">Grupo</span>
            <span style="font-weight: bold; color: #1a1a1a;">${safeText(payload.groupNumber)}</span>
          </div>
          <div style="display: flex; justify-content: space-between; align-items: center; padding: 4px 0; border-bottom: 1px solid #eee; font-size: 11px;">
            <span style="color: #6d6d6d;">Crédito</span>
            <span style="font-weight: bold; color: #1a1a1a;">${formatCurrency(payload.valorCredito)}</span>
          </div>
          <div style="display: flex; justify-content: space-between; align-items: center; padding: 4px 0; border-bottom: 1px solid #eee; font-size: 11px;">
            <span style="color: #6d6d6d;">Parcela</span>
            <span style="font-weight: bold; color: #2d9f4f;">${formatCurrency(parcelaSelecionada)}</span>
          </div>
          <div style="display: flex; justify-content: space-between; align-items: center; padding: 4px 0; border-bottom: 1px solid #eee; font-size: 11px;">
            <span style="color: #6d6d6d;">Cotas</span>
            <span style="font-weight: bold; color: #1a1a1a;">${safeText(payload.juncaoCota)}</span>
          </div>
          ${payload.seguro && String(payload.seguro).toLowerCase() === 'sim' ? `
          <div style="display: flex; justify-content: space-between; align-items: center; padding: 4px 0; border-bottom: 1px solid #eee; font-size: 11px;">
            <span style="color: #6d6d6d;">Seguro</span>
            <span style="font-weight: bold; color: #1a1a1a;">Sim</span>
          </div>
          ` : ''}
          <div style="display: flex; justify-content: space-between; align-items: center; padding: 4px 0; font-size: 11px;">
            <span style="color: #6d6d6d;">Prazo</span>
            <span style="font-weight: bold; color: #1a1a1a;">${payload.prazo}</span>
          </div>
        </div>
      </div>
      <!-- Contato à direita -->
      <div style="flex: 0 0 50%; display: flex; align-items: stretch; justify-content: center;">
        <div style="background: #354660; color: white; border-radius: 10px; padding: 6px; text-align: center; width: 85%; display: flex; flex-direction: column; justify-content: center;">
          <div style="font-size: 11px; font-weight: bold; margin-bottom: 1px;">${safeText(userInfo.name)}</div>
          <div style="font-size: 7px; margin-bottom: 4px; opacity: 0.95;">Especialista em Consórcio</div>
          <div style="font-size: 10px; margin-bottom: 2px; display: flex; align-items: center; justify-content: center; gap: 3px;">
            <span>✉</span>
            <span>${safeText(userInfo.email)}</span>
          </div>
          ${userInfo.phone ? `
          <div style="font-size: 10px; display: flex; align-items: center; justify-content: center; gap: 3px;">
            <span>📱</span>
            <span>${safeText(userInfo.phone)}</span>
          </div>
          ` : ''}
        </div>
      </div>
    </div>
  `;

  // Simulação Sorteio
  const sorteioHtml = `
    <div style="margin-bottom: 8px;">
      <h3 style="font-size: 11px; font-weight: bold; color: #1a1a1a; margin: 0 0 4px 0;">Simulação de contemplação por Sorteio</h3>
      <p style="font-size: 10px; color: #6d6d6d; margin: 0 0 6px 0; line-height: 1.3;">Crédito contratado disponível por inteiro, sem lance.</p>
      <div style="border-left: 3px solid #354660; padding-left: 8px; font-size: 10px;">
        <div style="display: flex; justify-content: space-between; margin-bottom: 2px;">
          <span>Crédito disponível</span>
          <span style="font-weight: bold;">${formatCurrency(payload.creditoSorteio)}</span>
        </div>
        <div style="display: flex; justify-content: space-between; margin-bottom: 2px;">
          <span>Contemplação simulada</span>
          <span style="font-weight: bold;">Mês ${payload.mesesContemplacao || 1}</span>
        </div>
        <div style="display: flex; justify-content: space-between;">
          <span>Parcela pós</span>
          <span style="font-weight: bold;">${parcelasRestantesSorteio}x de ${formatCurrency(payload.parcelaAPagarSorteio)}</span>
        </div>
      </div>
    </div>
  `;

  // Simulação Lance Fixo (condicional)
  const lanceFixoHtml = payload.lanceFixoAtivo ? `
    <div style="margin-bottom: 8px;">
      <h3 style="font-size: 11px; font-weight: bold; color: #1a1a1a; margin: 0 0 4px 0;">Simulação de contemplação por Lance Fixo</h3>
      <p style="font-size: 10px; color: #6d6d6d; margin: 0 0 6px 0; line-height: 1.3;">Lance fixo reduz o crédito disponível, mas diminui o saldo devedor após contemplação.</p>
      <div style="border-left: 3px solid #354660; padding-left: 8px; font-size: 10px;">
        <div style="display: flex; justify-content: space-between; margin-bottom: 2px;">
          <span>Crédito disponível</span>
          <span style="font-weight: bold;">${formatCurrency(payload.creditoLanceFixo)}</span>
        </div>
        <div style="display: flex; justify-content: space-between; margin-bottom: 2px;">
          <span>Lance fixo (${payload.lanceFixoPercent ?? payload.lanceEmbutido}%)</span>
          <span style="font-weight: bold;">${formatCurrency(payload.valorLanceFixo ?? payload.valorLanceEmbutido)}</span>
        </div>
        <div style="display: flex; justify-content: space-between; margin-bottom: 2px;">
          <span>Contemplação simulada</span>
          <span style="font-weight: bold;">Mês ${payload.mesesContemplacao || 1}</span>
        </div>
        <div style="display: flex; justify-content: space-between;">
          <span>Parcela pós</span>
          <span style="font-weight: bold;">${parcelasRestantesLanceFixo}x de ${formatCurrency(payload.parcelaAPagarLanceFixo)}</span>
        </div>
      </div>
    </div>
  ` : '';

  // Simulação Lance Fidelidade (condicional). Mesmo bloco do Lance Fixo, porque
  // é a mesma modalidade com outro percentual — se um dia divergirem, é aqui e
  // no simulador que precisa mudar junto.
  const lanceFidelidadeHtml = payload.lanceFidelidadeAtivo ? `
    <div style="margin-bottom: 8px;">
      <h3 style="font-size: 11px; font-weight: bold; color: #1a1a1a; margin: 0 0 4px 0;">Simulação de contemplação por Lance Fidelidade</h3>
      <p style="font-size: 10px; color: #6d6d6d; margin: 0 0 6px 0; line-height: 1.3;">Lance fidelidade reduz o crédito disponível, mas diminui o saldo devedor após contemplação.</p>
      <div style="border-left: 3px solid #354660; padding-left: 8px; font-size: 10px;">
        <div style="display: flex; justify-content: space-between; margin-bottom: 2px;">
          <span>Crédito disponível</span>
          <span style="font-weight: bold;">${formatCurrency(payload.creditoLanceFidelidade)}</span>
        </div>
        <div style="display: flex; justify-content: space-between; margin-bottom: 2px;">
          <span>Lance fidelidade (${payload.lanceFidelidadePercent ?? 0}%)</span>
          <span style="font-weight: bold;">${formatCurrency(payload.valorLanceFidelidade)}</span>
        </div>
        <div style="display: flex; justify-content: space-between; margin-bottom: 2px;">
          <span>Contemplação simulada</span>
          <span style="font-weight: bold;">Mês ${payload.mesesContemplacao || 1}</span>
        </div>
        <div style="display: flex; justify-content: space-between;">
          <span>Parcela pós</span>
          <span style="font-weight: bold;">${payload.parcelasRestantesLanceFidelidade ?? 0}x de ${formatCurrency(payload.parcelaAPagarLanceFidelidade)}</span>
        </div>
      </div>
    </div>
  ` : '';

  // Simulação Lance Livre (condicional)
  const lanceHtml = payload.lanceLivreAtivo ? `
    <div style="margin-bottom: 8px;">
      <h3 style="font-size: 11px; font-weight: bold; color: #1a1a1a; margin: 0 0 4px 0;">Simulação de contemplação por Lance Livre</h3>
      <p style="font-size: 10px; color: #6d6d6d; margin: 0 0 6px 0; line-height: 1.3;">Contempla quem oferta o maior percentual do plano.</p>
      <div style="border-left: 3px solid #354660; padding-left: 8px; font-size: 10px;">
        <div style="display: flex; justify-content: space-between; margin-bottom: 2px;">
          <span>Crédito disponível</span>
          <span style="font-weight: bold;">${formatCurrency(payload.creditoLanceLivre)}</span>
        </div>
        <div style="display: flex; justify-content: space-between; margin-bottom: 2px;">
          <span>Lance embutido (${payload.lanceEmbutido}%)</span>
          <span style="font-weight: bold;">${formatCurrency(payload.valorLanceEmbutido)}</span>
        </div>
        <div style="display: flex; justify-content: space-between; margin-bottom: 2px;">
          <span>Lance pago (${payload.lanceLivre}%)</span>
          <span style="font-weight: bold;">${formatCurrency(payload.valorLanceLivre)}</span>
        </div>
        <div style="display: flex; justify-content: space-between; margin-bottom: 2px;">
          <span>Contemplação simulada</span>
          <span style="font-weight: bold;">Mês ${payload.mesesContemplacao || 1}</span>
        </div>
        <div style="display: flex; justify-content: space-between;">
          <span>Parcela pós</span>
          <span style="font-weight: bold;">${parcelasRestantesLanceLivre}x de ${formatCurrency(payload.parcelaAPagar)}</span>
        </div>
      </div>
    </div>
  ` : '';

  // Seção sobre Servopa
  const servicoHtml = `
    <div style="margin-bottom: 6px; font-size: 10px; line-height: 1.3; color: #4a4a4a;">
      <h3 style="font-size: 11px; font-weight: bold; color: #1a1a1a; margin: 0 0 4px 0;">Consórcio Servopa</h3>
      <p style="margin: 0 0 4px 0;">Fundado em 1966, o Consórcio Servopa é um dos maiores e mais tradicionais do Brasil e já atendeu milhares de clientes.</p>
      <p style="margin: 0;">Operações autorizadas e fiscalizadas pelo Banco Central do Brasil.</p>
    </div>
  `;

  // Card final promocional (simplificado)
  const promoCardHtml = ``;

  // Footer
  const footerHtml = `
    <div style="padding-top: 6px; font-size: 9px; color: #999; text-align: center;">
      North • Proposta gerada automaticamente • Valores e cotas sujeitos à disponibilidade e a alteração.
    </div>
  `;

  container.innerHTML =
    headerHtml +
    sloganHtml +
    fieldsAndContactHtml +
    sorteioHtml +
    lanceFixoHtml +
    lanceFidelidadeHtml +
    lanceHtml +
    servicoHtml +
    promoCardHtml +
    footerHtml;

  document.body.appendChild(container);

  // Wait for images to load

  await waitForImages(container);
  await new Promise((resolve) => requestAnimationFrame(() => resolve(null)));

  const filename = `proposta-north-${new Date().toISOString().split('T')[0]}.pdf`;
  try {
    const canvas = await html2canvas(container, {
      scale: 2,
      useCORS: true,
      backgroundColor: '#ffffff',
      windowWidth: 600,
      windowHeight: Math.max(container.scrollHeight, 1122),
    });

    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF({
      unit: 'mm',
      format: 'a4',
      orientation: 'portrait',
    });

    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const imgProps = pdf.getImageProperties(imgData);
    const imgWidth = pageWidth;
    let imgHeight = (imgProps.height * imgWidth) / imgProps.width;
    
    // Se a imagem for maior que a página, redimensiona para caber
    if (imgHeight > pageHeight) {
      const scaleFactor = pageHeight / imgHeight;
      imgHeight = pageHeight;
      const newImgWidth = imgWidth * scaleFactor;
      const xOffset = (pageWidth - newImgWidth) / 2;
      pdf.addImage(imgData, 'PNG', xOffset, 0, newImgWidth, imgHeight);
    } else {
      // Se couber, centraliza verticalmente
      const yOffset = (pageHeight - imgHeight) / 2;
      pdf.addImage(imgData, 'PNG', 0, yOffset, imgWidth, imgHeight);
    }

    pdf.save(filename);
  } finally {
    document.body.removeChild(container);
  }
};

export const generateMultiCotasPdf = async (
  data: MultiCotasData,
  userInfo: UserInfo,
) => {
  const { resumo_projeto: rp, cotas, clientName } = data;
  const origin = window.location.origin;

  /**
   * Uma cota não é um projeto.
   *
   * O layout inteiro deste PDF é sobre SOMAR: "Crédito Contratado Total",
   * "Quantidade de Cotas", linha TOTAL no fim da tabela. Com uma cota só, cada
   * um desses totais é a própria cota repetida — o documento fica dizendo duas
   * vezes a mesma coisa e ainda chama de "projeto estruturado" o que é uma
   * proposta simples. Pedido do Rick: cotas > 1 é projeto, senão não.
   *
   * O que muda é só a moldura (título e rótulos). Os números são os mesmos, e
   * a linha TOTAL nem existe: a tool só emite quando há mais de uma cota.
   */
  const cotaUnica = Number(rp.quantidade_cotas ?? 0) <= 1;
  const T = (rotulo: string) => (cotaUnica ? rotulo : `${rotulo} total`);

  const container = document.createElement('div');
  container.style.position = 'fixed';
  container.style.left = '-9999px';
  container.style.top = '-9999px';
  container.style.width = '794px'; // A4 at 96dpi
  container.style.padding = '32px';
  container.style.background = '#ffffff';
  container.style.color = '#2a2a2a';
  container.style.fontSize = '12px';
  container.style.lineHeight = '1.4';
  container.style.pointerEvents = 'none';
  container.style.zIndex = '9999';
  container.style.fontFamily = 'Arial, sans-serif';
  container.style.boxSizing = 'border-box';

  const headerHtml = `
    <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:16px;padding-bottom:12px;border-bottom:2px solid #354660;">
      <img src="${origin}/logos/logo_full.png" alt="North" style="height:38px;object-fit:contain;" />
      <div style="text-align:center;">
        <div style="font-size:17px;font-weight:700;color:#354660;letter-spacing:0.04em;">${cotaUnica ? 'PROPOSTA' : 'PROJETO ESTRUTURADO'}</div>
        ${clientName ? `<div style="font-size:11px;color:#555;margin-top:2px;">Para: ${clientName}</div>` : ''}
      </div>
      <img src="${origin}/logos/servopa_parceiro.webp" alt="Servopa" style="height:38px;object-fit:contain;" />
    </div>
  `;

  const summaryHtml = `
    <div style="display:flex;gap:12px;margin-bottom:18px;">
      <div style="flex:1;background:#354660;color:#fff;border-radius:10px;padding:14px 16px;">
        <div style="font-size:10px;font-weight:700;letter-spacing:0.1em;text-transform:uppercase;margin-bottom:10px;opacity:0.8;">${cotaUnica ? 'Contratação' : 'Início do projeto'}</div>
        <div style="display:flex;flex-direction:column;gap:6px;font-size:11px;">
          <div style="display:flex;justify-content:space-between;"><span style="opacity:0.8;">${T('Crédito contratado')}</span><span style="font-weight:700;">${rp.credito_contratado_total}</span></div>
          <div style="display:flex;justify-content:space-between;"><span style="opacity:0.8;">${cotaUnica ? 'Parcela inicial' : 'Parcela inicial total'}</span><span style="font-weight:700;">${rp.parcela_inicial_total}</span></div>
          ${cotaUnica ? '' : `<div style="display:flex;justify-content:space-between;"><span style="opacity:0.8;">Quantidade de cotas</span><span style="font-weight:700;">${rp.quantidade_cotas}</span></div>`}
          <div style="display:flex;justify-content:space-between;"><span style="opacity:0.8;">${T('Lance pago')}</span><span style="font-weight:700;">${rp.lance_pago_total}</span></div>
          <div style="display:flex;justify-content:space-between;"><span style="opacity:0.8;">${T('Lance embutido')}</span><span style="font-weight:700;">${rp.lance_embutido_total}</span></div>
        </div>
      </div>
      <div style="flex:1;background:#f0f4f8;border:1px solid #d0dae6;border-radius:10px;padding:14px 16px;">
        <div style="font-size:10px;font-weight:700;letter-spacing:0.1em;text-transform:uppercase;margin-bottom:10px;color:#354660;">${cotaUnica ? 'Após a contemplação' : 'Final do projeto'}</div>
        <div style="display:flex;flex-direction:column;gap:6px;font-size:11px;">
          <div style="display:flex;justify-content:space-between;"><span style="color:#555;">${cotaUnica ? 'Saldo devedor inicial' : 'Saldo devedor inicial total'}</span><span style="font-weight:700;">${rp.saldo_devedor_inicial_total}</span></div>
          <div style="display:flex;justify-content:space-between;"><span style="color:#555;">${T('Crédito liberado')}</span><span style="font-weight:700;">${rp.credito_liberado_total}</span></div>
          <div style="display:flex;justify-content:space-between;"><span style="color:#555;">${T('Crédito líquido')}</span><span style="font-weight:700;color:#1a7a40;">${rp.credito_liquido_total}</span></div>
          <div style="display:flex;justify-content:space-between;"><span style="color:#555;">${cotaUnica ? 'Parcela pós-contemplação' : 'Parcela pós-contemplação total'}</span><span style="font-weight:700;">${rp.parcela_pos_total}</span></div>
        </div>
      </div>
    </div>
  `;

  const thStyle = 'background:#354660;color:#fff;padding:7px 6px;font-size:9.5px;font-weight:700;text-align:center;white-space:nowrap;';
  const tdStyleBase = 'padding:6px 5px;font-size:9.5px;text-align:center;border-bottom:1px solid #e8edf3;';

  // Expande grupos de cotas idênticas em uma linha por cota e renumera
  // sequencialmente. A linha TOTAL (sintética emitida pela tool com
  // numero <= 0 ou grupo contendo "TOTAL") é preservada como está e
  // renderizada com estilo próprio (sem `-1` na coluna Nº).
  const isTotalRow = (c: MultiCotasCota) => {
    if (typeof c.numero === 'number' && c.numero <= 0) return true;
    if (typeof c.grupo === 'string' && c.grupo.includes('TOTAL')) return true;
    return false;
  };

  const expandedCotas: MultiCotasCota[] = [];
  let displayNum = 1;
  for (const c of cotas) {
    if (isTotalRow(c)) {
      expandedCotas.push(c);
      continue;
    }
    const qty = typeof c.quantidade === 'number' && c.quantidade > 1 ? c.quantidade : 1;
    for (let i = 0; i < qty; i++) {
      expandedCotas.push({ ...c, numero: displayNum });
      displayNum++;
    }
  }

  const tableRows = expandedCotas.map((c, idx) => {
    const total = isTotalRow(c);
    const bg = total ? '#e8edf3' : (idx % 2 === 0 ? '#ffffff' : '#f5f8fc');
    const fontWeight = total ? 'font-weight:700;' : '';
    const td = (v: string | number | undefined, extra = '') =>
      `<td style="${tdStyleBase}background:${bg};${fontWeight}${extra}">${safeText(v)}</td>`;
    const numeroDisplay = total ? '' : (c.numero ?? '');
    const grupoDisplay = total ? 'TOTAL' : (c.grupo ?? '-');
    return `<tr>
      ${td(numeroDisplay)}
      ${td(c.mes)}
      ${td(c.prazo)}
      ${td(grupoDisplay)}
      ${td(c.credito)}
      ${td(c.parcela)}
      ${td(c.lance_pago)}
      ${td(c.lance_embutido)}
      ${td(c.credito_liberado)}
      ${td(c.credito_liquido, total ? 'color:#1a7a40;' : 'color:#1a7a40;font-weight:700;')}
      ${td(c.parcela_pos)}
      ${td(c.saldo_devedor)}
    </tr>`;
  }).join('');

  const tableHtml = `
    <div style="margin-bottom:16px;overflow:hidden;border-radius:8px;border:1px solid #d0dae6;">
      <table style="width:100%;border-collapse:collapse;">
        <thead>
          <tr>
            <th style="${thStyle}border-radius:8px 0 0 0;">Nº</th>
            <th style="${thStyle}">Mês</th>
            <th style="${thStyle}">Prazo</th>
            <th style="${thStyle}">Grupo</th>
            <th style="${thStyle}">Crédito</th>
            <th style="${thStyle}">Parcela</th>
            <th style="${thStyle}">L.Pago</th>
            <th style="${thStyle}">L.Embutido</th>
            <th style="${thStyle}">Créd.Lib.</th>
            <th style="${thStyle}">Créd.Líq.</th>
            <th style="${thStyle}">Parc.Pós</th>
            <th style="${thStyle}border-radius:0 8px 0 0;">Saldo Dev.</th>
          </tr>
        </thead>
        <tbody>${tableRows}</tbody>
      </table>
    </div>
  `;

  const obsHtml = `
    <div style="margin-bottom:16px;font-size:10px;color:#555;background:#fffbf0;border:1px solid #e6d89a;border-radius:8px;padding:10px 14px;">
      <strong style="color:#7a5c00;">Observações:</strong> Esta é uma simulação. Disponibilidade de grupos e cotas sujeita a vagas no momento da adesão. Valores de parcelas e lances podem sofrer alteração conforme atualização da administradora. Lance embutido reduz o crédito disponível para uso.
    </div>
  `;

  const contactHtml = `
    <div style="display:flex;justify-content:flex-end;margin-bottom:12px;">
      <div style="background:#354660;color:#fff;border-radius:10px;padding:10px 16px;text-align:center;min-width:180px;">
        <div style="font-size:12px;font-weight:700;margin-bottom:1px;">${safeText(userInfo.name)}</div>
        <div style="font-size:9px;opacity:0.8;margin-bottom:5px;">Especialista em Consórcio</div>
        <div style="font-size:10px;margin-bottom:2px;">✉ ${safeText(userInfo.email)}</div>
        ${userInfo.phone ? `<div style="font-size:10px;">📱 ${safeText(userInfo.phone)}</div>` : ''}
      </div>
    </div>
  `;

  const footerHtml = `
    <div style="padding-top:8px;border-top:1px solid #e0e7ef;font-size:9px;color:#999;text-align:center;">
      North • Proposta gerada automaticamente em ${new Date().toLocaleDateString('pt-BR')} • Valores sujeitos à disponibilidade e alteração.
    </div>
  `;

  container.innerHTML = headerHtml + summaryHtml + tableHtml + obsHtml + contactHtml + footerHtml;
  document.body.appendChild(container);


  await waitForImages(container);
  await new Promise((resolve) => requestAnimationFrame(() => resolve(null)));

  const filename = `projeto-estruturado-north-${new Date().toISOString().split('T')[0]}.pdf`;

  await renderContainerToPdf(container, filename);
};
export type LinhaComparativa = {
  opcao: number;
  grupo: number | string;
  prazo: string;
  credito: string;
  parcela: string;
  flex: string;
  taxa_admin: string;
  segmento?: string;
  seguro?: string;
  aviso?: string;
};

export type TabelaComparativaData = {
  linhas: LinhaComparativa[];
  clientName?: string;
};

/**
 * PDF da tabela de OPÇÕES — alternativas para o cliente escolher UMA.
 *
 * ── POR QUE NÃO REUSA generateMultiCotasPdf ─────────────────────────────────
 * Aquele imprime "Crédito Contratado Total", "Parcela Total Inicial" e
 * "Quantidade de Cotas" porque lá as cotas SOMAM: o cliente leva todas. Aqui as
 * linhas se excluem. Três variações da mesma cota de R$ 1M sairiam como
 * "R$ 3.000.000 contratados" — um número que não existe, e do tipo que o
 * cliente lê rápido e leva embora errado.
 *
 * Então: sem bloco de resumo, sem linha de total, e o título diz OPÇÕES.
 *
 * O `aviso` de cada linha (crédito aproximado) vai impresso no rodapé, não
 * escondido: o cliente pediu 1 milhão e a linha entrega 1.043.200 — omitir isso
 * num documento que ele leva pra casa é o erro caro.
 */
export const generateTabelaComparativaPdf = async (
  data: TabelaComparativaData,
  userInfo: UserInfo,
) => {
  const { linhas, clientName } = data;
  const origin = window.location.origin;

  const container = document.createElement('div');
  container.style.position = 'fixed';
  container.style.left = '-9999px';
  container.style.top = '-9999px';
  container.style.width = '794px';
  container.style.padding = '32px';
  container.style.background = '#ffffff';
  container.style.color = '#2a2a2a';
  container.style.fontSize = '12px';
  container.style.lineHeight = '1.4';
  container.style.pointerEvents = 'none';
  container.style.zIndex = '9999';
  container.style.fontFamily = 'Arial, sans-serif';
  container.style.boxSizing = 'border-box';

  const escapar = (v: unknown) =>
    String(v ?? '').replace(/[&<>"]/g, (c) =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c] as string));

  const cabecalho = `
    <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:16px;padding-bottom:12px;border-bottom:2px solid #354660;">
      <img src="${origin}/logos/logo_full.png" alt="North" style="height:38px;object-fit:contain;" />
      <div style="text-align:center;">
        <div style="font-size:17px;font-weight:700;color:#354660;letter-spacing:0.04em;">OPÇÕES DE COTA</div>
        ${clientName ? `<div style="font-size:11px;color:#555;margin-top:2px;">Para: ${escapar(clientName)}</div>` : ''}
      </div>
      <img src="${origin}/logos/servopa_parceiro.webp" alt="Servopa" style="height:38px;object-fit:contain;" />
    </div>
  `;

  const th = 'padding:9px 8px;font-size:10px;font-weight:700;letter-spacing:0.04em;text-transform:uppercase;color:#fff;background:#354660;text-align:left;';
  const td = 'padding:9px 8px;font-size:11px;border-bottom:1px solid #e4e9ef;';

  const corpo = linhas
    .map(
      (l, i) => `
      <tr style="background:${i % 2 === 0 ? '#ffffff' : '#f7f9fb'};">
        <td style="${td}font-weight:700;color:#354660;">${escapar(l.opcao)}</td>
        <td style="${td}">${escapar(l.grupo)}</td>
        <td style="${td}">${escapar(l.prazo)}</td>
        <td style="${td}font-weight:600;">${escapar(l.credito)}</td>
        <td style="${td}font-weight:700;color:#354660;">${escapar(l.parcela)}</td>
        <td style="${td}">${escapar(l.flex)}</td>
        <td style="${td}">${escapar(l.taxa_admin)}</td>
      </tr>`,
    )
    .join('');

  const avisos = linhas.filter((l) => l.aviso);
  const blocoAvisos = avisos.length
    ? `<div style="margin-top:14px;padding:10px 12px;background:#fff8e6;border:1px solid #f0d79a;border-radius:8px;">
         <div style="font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:0.06em;color:#8a6100;margin-bottom:6px;">Observações</div>
         ${avisos
           .map((l) => `<div style="font-size:10px;color:#6b4e00;">Opção ${escapar(l.opcao)}: ${escapar(l.aviso)}</div>`)
           .join('')}
       </div>`
    : '';

  container.innerHTML = `
    ${cabecalho}
    <div style="font-size:11px;color:#555;margin-bottom:12px;">
      Cada linha é uma <strong>alternativa</strong>: os valores não se somam.
    </div>
    <table style="width:100%;border-collapse:collapse;border-radius:8px;overflow:hidden;">
      <thead>
        <tr>
          <th style="${th}">Opção</th>
          <th style="${th}">Grupo</th>
          <th style="${th}">Prazo</th>
          <th style="${th}">Crédito</th>
          <th style="${th}">Parcela</th>
          <th style="${th}">Flex</th>
          <th style="${th}">Taxa adm.</th>
        </tr>
      </thead>
      <tbody>${corpo}</tbody>
    </table>
    ${blocoAvisos}
    <div style="margin-top:18px;padding-top:12px;border-top:1px solid #e4e9ef;font-size:10px;color:#666;">
      <div>${escapar(userInfo?.name ?? '')}${userInfo?.phone ? ` · ${escapar(userInfo.phone)}` : ''}${userInfo?.email ? ` · ${escapar(userInfo.email)}` : ''}</div>
      <div style="margin-top:4px;">Valores sujeitos a alteração e à disponibilidade de vagas no grupo. Simulação sem valor contratual.</div>
    </div>
  `;

  document.body.appendChild(container);
  await waitForImages(container);
  await new Promise((resolve) => requestAnimationFrame(() => resolve(null)));

  await renderContainerToPdf(
    container,
    `opcoes-north-${new Date().toISOString().split('T')[0]}.pdf`,
  );
};
